import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../services/api_config';

// Life & Family: optional, same family shape as SAFE and Emit.
const MP_EMPTY_FAMILY = { relationship: '', partner_role: '', partner_name: '', children: [], grandchildren: [], other: '' };
const MP_EMPTY_LIFE = { family: MP_EMPTY_FAMILY, current_situation: '', working_toward: [], military_status: '' };
const MP_RELATIONSHIP = [
  { value: 'married', label: 'Married' }, { value: 'relationship', label: 'In a relationship' },
  { value: 'single', label: 'Single' }, { value: 'separated', label: 'Separated or divorced' },
  { value: 'widowed', label: 'Widowed' }
];
const MP_ROLES = [{ value: 'husband', label: 'Husband' }, { value: 'wife', label: 'Wife' }, { value: 'partner', label: 'Partner' }];
const MP_SITUATION = [
  { value: 'working', label: 'Working' }, { value: 'school', label: 'In school or training' },
  { value: 'job_seeking', label: 'Looking for work' }, { value: 'own_business', label: 'Running my own business' },
  { value: 'caregiving', label: 'Caring for family' }, { value: 'retired', label: 'Retired' },
  { value: 'not_working', label: 'Not working right now' }
];
const MP_TOWARD = [
  { value: 'work', label: 'Steady work or a new career' }, { value: 'school', label: 'Finishing school or training' },
  { value: 'build', label: 'Building something of my own' }, { value: 'family', label: 'Being there for my family' },
  { value: 'health', label: 'Getting healthier' }, { value: 'people', label: 'Reconnecting with people' },
  { value: 'purpose', label: 'A sense of purpose' }, { value: 'getting_through', label: 'Getting through this stretch' },
  { value: 'unsure', label: 'Not sure yet' }
];
const MP_MILITARY = [
  { value: 'none', label: 'No' }, { value: 'active', label: 'Active duty' },
  { value: 'veteran', label: 'Veteran' }, { value: 'military_family', label: 'Military family' }
];

const MentalPerformanceOnboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  // Each new page starts at the top.
  useEffect(() => {
    try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch (e) { window.scrollTo(0, 0); }
  }, [step]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState({
    program: '',
    performance_domain: '',
    domain_context: {},
    sport: '',
    position: '',
    competitive_level: '',
    years_experience: null,
    goals: [],
    challenges: [],
    preferred_name: '',
    coach_tone: 'motivating',
    faith_background: '',
    life_context: null
  });

  // Total steps based on whether we need domain-specific details
  const totalSteps = 6;

  useEffect(() => {
    // Check if user has existing profile (edit mode)
    API.get('/api/v1/mental-performance/profile').then(res => {
      if (res.data.exists) {
        setIsEditing(true);
        setProfile({
          program: res.data.program || '',
          performance_domain: res.data.performance_domain || 'athlete',
          domain_context: res.data.domain_context || {},
          sport: res.data.sport || '',
          position: res.data.position || '',
          competitive_level: res.data.competitive_level || '',
          years_experience: res.data.years_experience || null,
          goals: res.data.goals || [],
          challenges: res.data.challenges || [],
          preferred_name: res.data.preferred_name || '',
          coach_tone: res.data.coach_tone || 'motivating',
          faith_background: res.data.faith_background || '',
          life_context: res.data.life_context || null
        });
      }
    }).catch(() => {});
  }, []);

  // ============================================================================
  // DOMAIN CONFIGURATION
  // ============================================================================
  
  const domains = [
    {
      value: 'athlete',
      name: 'Athlete',
      emoji: '🏆',
      description: 'Competitive sports at any level',
      examples: 'Basketball, football, tennis, golf, swimming, etc.'
    },
    {
      value: 'coach',
      name: 'Coach',
      emoji: '\ud83d\udccb',
      description: 'You coach others: sports coaches and executive coaches',
      examples: 'Game day, leading a team, coaching leaders, building a practice'
    },
    {
      value: 'executive',
      name: 'Executive / Business Leader',
      emoji: '💼',
      description: 'You lead people: C-suite, directors, managers, team leads',
      examples: 'Board presentations, negotiations, leadership decisions'
    },
    {
      value: 'communications',
      name: 'Communications Executive',
      emoji: '\uD83D\uDCE3',
      description: 'Communications, PR and media relations leaders, spokespeople',
      examples: 'Press interviews, crisis response, advising leadership'
    },
    {
      value: 'entrepreneur',
      name: 'Entrepreneur',
      emoji: '🚀',
      description: 'Founders, business owners, startup leaders',
      examples: 'Pitch meetings, product launches, investor relations'
    },
    {
      value: 'performer',
      name: 'Performer / Artist',
      emoji: '🎭',
      description: 'Musicians, actors, speakers, content creators',
      examples: 'Auditions, stage performance, public speaking'
    },
    {
      value: 'student',
      name: 'Student / Academic',
      emoji: '🎓',
      description: 'High-stakes exams, interviews, academic performance',
      examples: 'Bar exam, medical boards, job interviews, dissertations'
    },
    {
      value: 'sales',
      name: 'Sales Professional',
      emoji: '🤝',
      description: 'Closing deals, client presentations, quotas',
      examples: 'Sales calls, demos, rejection resilience'
    },
    {
      value: 'medical',
      name: 'Medical Professional',
      emoji: '⚕️',
      description: 'Surgeons, physicians, first responders',
      examples: 'Operating room decisions, crisis management'
    },
    {
      value: 'educator',
      name: 'Educator',
      emoji: '📚',
      description: 'Teachers, professors, administrators',
      examples: 'Classroom management, parent conferences, evaluations'
    },
    {
      value: 'legal',
      name: 'Legal Professional',
      emoji: '⚖️',
      description: 'Attorneys, paralegals, judges',
      examples: 'Courtroom arguments, depositions, negotiations'
    },
    {
      value: 'transportation',
      name: 'Transportation',
      emoji: '✈️',
      description: 'Airlines, trucking, rail, maritime, logistics',
      examples: 'Pilots, flight attendants, air traffic control, drivers'
    },
    {
      value: 'chaplain',
      name: 'Chaplain / Spiritual Care',
      emoji: '🕊️',
      description: 'Corporate, hospital, military, prison, or sports chaplains',
      examples: 'Crisis intervention, spiritual care under pressure, ministry sustainability'
    },
    {
      value: 'other',
      name: 'Other Industry',
      emoji: '💡',
      description: 'Any industry or role not listed above',
      examples: 'Tell us your specific industry and job'
    }
  ];

  // ============================================================================
  // ATHLETE CONFIGURATION
  // ============================================================================

  const sports = [
    {name:'Basketball',emoji:'🏀'},{name:'Football',emoji:'🏈'},{name:'Baseball',emoji:'⚾'},
    {name:'Soccer',emoji:'⚽'},{name:'Tennis',emoji:'🎾'},{name:'Golf',emoji:'⛳'},
    {name:'Swimming',emoji:'🏊'},{name:'Track & Field',emoji:'🏃'},{name:'Volleyball',emoji:'🏐'},
    {name:'Wrestling',emoji:'🤼'},{name:'Hockey',emoji:'🏒'},{name:'Softball',emoji:'🥎'},
    {name:'Gymnastics',emoji:'🤸'},{name:'MMA/Boxing',emoji:'🥊'},{name:'Cross Country',emoji:'🏃'},
    {name:'Lacrosse',emoji:'🥍'},{name:'Cycling',emoji:'🚴'},{name:'Triathlon',emoji:'🏊‍♂️'},
    {name:'Rowing',emoji:'🚣'},{name:'Other',emoji:'🏆'}
  ];

  // ============================================================================
  // COACH CONFIGURATION
  // ============================================================================

  const coachTypes = [
    { value: 'sports', label: 'Sports Coaching', emoji: '\ud83c\udfc5', note: 'Teams, athletes, staff' },
    { value: 'executive', label: 'Executive Coaching', emoji: '\ud83d\udcbc', note: 'Leaders, executives, teams at work' }
  ];
  const sportsCoachRoles = [
    { value: 'head', label: 'Head Coach' },
    { value: 'assistant', label: 'Assistant Coach' },
    { value: 'coordinator', label: 'Coordinator' },
    { value: 'skills', label: 'Skills Coach' },
    { value: 'strength', label: 'Strength & Conditioning Coach' },
    { value: 'physical_trainer', label: 'Physical Trainer' },
    { value: 'athletic_trainer', label: 'Athletic Trainer' },
    { value: 'other', label: 'Other Coaching Role' }
  ];
  const sportsCoachLevels = [
    { value: 'youth', label: 'Youth' }, { value: 'high_school', label: 'High School' },
    { value: 'college', label: 'College' }, { value: 'professional', label: 'Professional' },
    { value: 'club', label: 'Club / Travel' }
  ];
  const execCoachFocus = [
    { value: 'leadership', label: 'Leadership' },
    { value: 'presence', label: 'Executive Presence & Communication' },
    { value: 'transition', label: 'Career Transition' },
    { value: 'team', label: 'Team & Organization' },
    { value: 'performance', label: 'Performance & Productivity' },
    { value: 'other', label: 'Other' }
  ];
  const execCoachClients = ['C-Suite', 'Senior Leaders', 'Emerging Leaders', 'Teams'];
  const execCoachPractice = ['Independent Practice', 'Coaching Firm', 'In-House (Corporate)'];
  const chooseCoachType = (v) => setProfile(prev => (
    prev.domain_context.coach_type === v ? prev
      : { ...prev, domain_context: { coach_type: v }, goals: [], challenges: [] }
  ));

  const athleteLevels = [
    {value:'recreational',label:'Recreational'},
    {value:'high_school',label:'High School'},
    {value:'college',label:'College'},
    {value:'professional',label:'Professional'},
    {value:'masters',label:'Masters/Adult'}
  ];

  // ============================================================================
  // EXECUTIVE CONFIGURATION
  // ============================================================================

  const executiveIndustries = [
    'Technology', 'Finance', 'Healthcare', 'Consulting', 'Manufacturing',
    'Retail', 'Media', 'Non-profit', 'Government', 'Education', 'Other'
  ];

  const executiveRoles = [
    {value:'c_suite',label:'C-Suite (CEO, CFO, CTO, etc.)'},
    {value:'vp',label:'VP / Senior Director'},
    {value:'director',label:'Director'},
    {value:'manager',label:'Manager / Team Lead'},
    {value:'individual',label:'Individual Contributor (High-stakes role)'}
  ];

  const teamSizes = [
    {value:'1-10',label:'1-10 people'},
    {value:'11-50',label:'11-50 people'},
    {value:'51-200',label:'51-200 people'},
    {value:'200+',label:'200+ people'}
  ];

  // ============================================================================
  // ENTREPRENEUR CONFIGURATION
  // ============================================================================

  const entrepreneurStages = [
    {value:'idea',label:'Idea Stage',emoji:'💡'},
    {value:'mvp',label:'MVP / Pre-revenue',emoji:'🔧'},
    {value:'early_revenue',label:'Early Revenue',emoji:'🌱'},
    {value:'growth',label:'Growth Stage',emoji:'📈'},
    {value:'scale',label:'Scaling / Series B+',emoji:'🚀'},
    {value:'established',label:'Established Business',emoji:'🏢'}
  ];

  const entrepreneurIndustries = [
    'SaaS / Software', 'E-commerce', 'Fintech', 'Healthcare Tech', 'Consumer',
    'B2B Services', 'Hardware', 'Media / Content', 'Real Estate', 'Other'
  ];

  // ============================================================================
  // PERFORMER CONFIGURATION
  // ============================================================================

  const performerTypes = [
    {value:'musician',label:'Musician',emoji:'🎵'},
    {value:'actor',label:'Actor',emoji:'🎭'},
    {value:'dancer',label:'Dancer',emoji:'💃'},
    {value:'speaker',label:'Public Speaker',emoji:'🎤'},
    {value:'comedian',label:'Comedian',emoji:'😄'},
    {value:'content_creator',label:'Content Creator',emoji:'📱'},
    {value:'other',label:'Other Performer',emoji:'⭐'}
  ];

  const performerMediums = [
    'Live Stage', 'Film/TV', 'Recording Studio', 'Online/Streaming', 
    'Corporate Events', 'Competition', 'Other'
  ];

  // ============================================================================
  // STUDENT CONFIGURATION
  // ============================================================================

  const studentLevels = [
    {value:'high_school',label:'High School'},
    {value:'undergraduate',label:'Undergraduate'},
    {value:'graduate',label:'Graduate School'},
    {value:'professional_school',label:'Professional School (Law, Medical, etc.)'},
    {value:'certification',label:'Professional Certification'}
  ];

  const studentFields = [
    'Law', 'Medicine', 'Business/MBA', 'Engineering', 'Sciences',
    'Humanities', 'Education', 'Nursing', 'Other'
  ];

  const studentExams = [
    'Bar Exam', 'Medical Boards (USMLE, COMLEX)', 'MCAT', 'LSAT', 'GRE/GMAT',
    'CPA', 'Nursing Boards (NCLEX)', 'Dissertation Defense', 'Job Interviews', 'Other'
  ];

  // ============================================================================
  // SALES CONFIGURATION
  // ============================================================================

  const salesRoles = [
    {value:'sdr',label:'SDR / BDR'},
    {value:'account_executive',label:'Account Executive'},
    {value:'enterprise',label:'Enterprise Sales'},
    {value:'sales_manager',label:'Sales Manager'},
    {value:'founder_sales',label:'Founder doing Sales'},
    {value:'real_estate',label:'Real Estate Agent'},
    {value:'other',label:'Other Sales Role'}
  ];

  const salesTypes = [
    {value:'b2b',label:'B2B'},
    {value:'b2c',label:'B2C'},
    {value:'enterprise',label:'Enterprise'},
    {value:'smb',label:'SMB'},
    {value:'transactional',label:'Transactional/Retail'}
  ];

  // ============================================================================
  // MEDICAL CONFIGURATION
  // ============================================================================

  const medicalSpecialties = [
    'Surgery', 'Emergency Medicine', 'Internal Medicine', 'Anesthesiology',
    'OB/GYN', 'Pediatrics', 'Psychiatry', 'Radiology', 'Primary Care',
    'Nursing', 'First Responder/EMT', 'Other'
  ];

  const medicalSettings = [
    {value:'hospital',label:'Hospital'},
    {value:'private_practice',label:'Private Practice'},
    {value:'academic',label:'Academic Medical Center'},
    {value:'emergency',label:'Emergency Services'},
    {value:'military',label:'Military/VA'},
    {value:'other',label:'Other'}
  ];

  const medicalRoles = [
    {value:'attending',label:'Attending Physician'},
    {value:'resident',label:'Resident'},
    {value:'fellow',label:'Fellow'},
    {value:'nurse',label:'Nurse (RN, NP)'},
    {value:'surgeon',label:'Surgeon'},
    {value:'first_responder',label:'First Responder/EMT'},
    {value:'other',label:'Other'}
  ];

  // ============================================================================
  // GOALS & CHALLENGES BY DOMAIN
  // ============================================================================

  // ============================================================================
  // EDUCATOR CONFIGURATION
  // ============================================================================
  const educatorLevels = [
    {value:'elementary',label:'Elementary School'},
    {value:'middle',label:'Middle School'},
    {value:'high',label:'High School'},
    {value:'college',label:'College/University'},
    {value:'admin',label:'Administration'},
    {value:'coach',label:'Athletic Coach'},
    {value:'other',label:'Other'}
  ];
  const educatorSubjects = [
    'Math', 'Science', 'English/Language Arts', 'History/Social Studies',
    'Physical Education', 'Arts/Music', 'Special Education', 'ESL',
    'Administration', 'Multiple Subjects', 'Other'
  ];
  const educatorSettings = [
    {value:'public',label:'Public School'},
    {value:'private',label:'Private School'},
    {value:'charter',label:'Charter School'},
    {value:'religious',label:'Religious/Faith-Based'},
    {value:'homeschool',label:'Homeschool Co-op'},
    {value:'higher_ed',label:'Higher Education'},
    {value:'other',label:'Other'}
  ];
  // ============================================================================
  // LEGAL CONFIGURATION
  // ============================================================================
  const legalPracticeAreas = [
    'Litigation', 'Corporate/Business', 'Criminal Defense', 'Family Law',
    'Personal Injury', 'Real Estate', 'Immigration', 'Intellectual Property',
    'Employment Law', 'Tax Law', 'General Practice', 'Other'
  ];
  const legalRoles = [
    {value:'partner',label:'Partner'},
    {value:'associate',label:'Associate'},
    {value:'solo',label:'Solo Practitioner'},
    {value:'in_house',label:'In-House Counsel'},
    {value:'public_defender',label:'Public Defender'},
    {value:'prosecutor',label:'Prosecutor'},
    {value:'judge',label:'Judge/Magistrate'},
    {value:'paralegal',label:'Paralegal'},
    {value:'other',label:'Other'}
  ];
  const legalSettings = [
    {value:'biglaw',label:'Large Firm (BigLaw)'},
    {value:'midsize',label:'Mid-Size Firm'},
    {value:'small',label:'Small Firm'},
    {value:'solo',label:'Solo Practice'},
    {value:'government',label:'Government'},
    {value:'nonprofit',label:'Non-Profit/Legal Aid'},
    {value:'corporate',label:'Corporate In-House'},
    {value:'other',label:'Other'}
  ];

  // ============================================================================
  // TRANSPORTATION CONFIGURATION
  // ============================================================================
  const transportationSectors = [
    {value:'airline',label:'Airlines',emoji:'✈️'},
    {value:'trucking',label:'Trucking/Freight',emoji:'🚛'},
    {value:'rail',label:'Rail/Train',emoji:'🚂'},
    {value:'maritime',label:'Maritime/Shipping',emoji:'🚢'},
    {value:'transit',label:'Public Transit',emoji:'🚌'},
    {value:'logistics',label:'Logistics/Warehousing',emoji:'📦'},
    {value:'rideshare',label:'Rideshare/Delivery',emoji:'🚗'},
    {value:'other',label:'Other Transportation',emoji:'🚁'}
  ];
  const airlineRoles = [
    {value:'pilot',label:'Pilot/First Officer'},
    {value:'flight_attendant',label:'Flight Attendant'},
    {value:'atc',label:'Air Traffic Controller'},
    {value:'dispatcher',label:'Flight Dispatcher'},
    {value:'ground_crew',label:'Ground Crew/Ramp Agent'},
    {value:'maintenance',label:'Aircraft Maintenance'},
    {value:'gate_agent',label:'Gate Agent/Customer Service'},
    {value:'cargo',label:'Cargo Handler'},
    {value:'management',label:'Operations Management'},
    {value:'other',label:'Other Airline Role'}
  ];
  const truckingRoles = [
    {value:'long_haul',label:'Long-Haul Driver'},
    {value:'local',label:'Local/Regional Driver'},
    {value:'owner_operator',label:'Owner-Operator'},
    {value:'dispatcher',label:'Dispatcher'},
    {value:'fleet_manager',label:'Fleet Manager'},
    {value:'safety',label:'Safety/Compliance'},
    {value:'other',label:'Other'}
  ];
  const transitRoles = [
    {value:'bus_driver',label:'Bus Driver'},
    {value:'train_operator',label:'Train Operator'},
    {value:'conductor',label:'Conductor'},
    {value:'dispatcher',label:'Dispatcher'},
    {value:'maintenance',label:'Maintenance'},
    {value:'supervisor',label:'Supervisor/Manager'},
    {value:'other',label:'Other'}
  ];
  const goalsByDomain = {
    athlete: [
      'Improve focus during competition', 'Build confidence', 'Perform under pressure',
      'Develop pre-game routines', 'Recover from mistakes', 'Access flow state',
      'Increase mental toughness', 'Better emotional control', 'Overcome fear of failure',
      'Improve consistency'
    ],
    executive: [
      'Stay calm in high-stakes meetings', 'Make better decisions under pressure',
      'Inspire and motivate my team', 'Handle difficult conversations',
      'Present with confidence', 'Manage stress without burnout',
      'Build executive presence', 'Navigate organizational politics',
      'Balance work-life demands', 'Lead through uncertainty'
    ],
    communications: [
      'Stay composed in a crisis', 'Speak with confidence on camera',
      'Handle tough press questions', 'Advise leadership under pressure',
      'Lead through a reputational crisis', 'Build executive presence',
      'Keep up with the 24/7 news cycle', 'Decide fast under deadline',
      'Prevent burnout', 'Lead my team'
    ],
    coach_sports: [
      'Stay composed on the sideline', 'Make better in-game decisions',
      'Lead and motivate my players', 'Handle parents and boosters',
      'Build team culture', 'Communicate with my staff',
      'Handle losing streaks', 'Manage job pressure',
      'Balance coaching and family', 'Prevent burnout'
    ],
    coach_executive: [
      'Be fully present with clients', 'Coach senior leaders with confidence',
      'Keep healthy boundaries', 'Grow my practice',
      'Handle difficult clients', 'Model what I teach',
      'Price and sell with confidence', 'Stay sharp and keep learning',
      'Manage my own stress', 'Prevent burnout'
    ],
    entrepreneur: [
      'Stay resilient through setbacks', 'Pitch with confidence',
      'Make decisions with incomplete information', 'Handle rejection and criticism',
      'Maintain focus amid chaos', 'Avoid founder burnout',
      'Lead my team effectively', 'Stay motivated during plateaus',
      'Trust my instincts', 'Balance urgency with patience'
    ],
    performer: [
      'Overcome stage fright', 'Handle audition nerves',
      'Stay present during performance', 'Recover from mistakes on stage',
      'Build confidence in my craft', 'Handle criticism and reviews',
      'Access creative flow', 'Manage pre-show anxiety',
      'Perform consistently', 'Deal with rejection'
    ],
    student: [
      'Manage test anxiety', 'Stay focused during exams',
      'Build confidence in my abilities', 'Handle pressure of high-stakes tests',
      'Overcome imposter syndrome', 'Develop effective study routines',
      'Stay motivated long-term', 'Manage perfectionism',
      'Handle setbacks and failures', 'Interview with confidence'
    ],
    sales: [
      'Handle rejection resilience', 'Close with confidence',
      'Stay motivated through slumps', 'Manage call anxiety',
      'Build rapport quickly', 'Handle objections calmly',
      'Maintain energy all day', 'Recover from lost deals',
      'Hit quota consistently', 'Present with authority'
    ],
    medical: [
      'Stay calm in emergencies', 'Make decisions under pressure',
      'Handle life-and-death stakes', 'Manage emotional toll',
      'Maintain focus during long procedures', 'Communicate with families',
      'Prevent burnout', 'Build confidence in skills',
      'Handle mistakes and complications', 'Lead medical teams'
    ],
    educator: [
      'Stay calm with disruptive students', 'Handle parent confrontations',
      'Perform during evaluations', 'Prevent teacher burnout',
      'Maintain classroom presence', 'Handle difficult conversations',
      'Build confidence in teaching', 'Manage work-life balance',
      'Stay motivated through challenges', 'Lead with patience'
    ],
    legal: [
      'Stay calm in courtroom', 'Handle cross-examination pressure',
      'Present arguments confidently', 'Manage client expectations',
      'Handle opposing counsel tactics', 'Prevent burnout',
      'Build courtroom presence', 'Make decisions under pressure',
      'Handle high-stakes negotiations', 'Lead legal teams'
    ],
    transportation: [
      'Stay calm during emergencies', 'Handle schedule pressure',
      'Manage passenger/customer interactions', 'Maintain focus on long shifts',
      'Handle weather and delay stress', 'Build confidence in skills',
      'Prevent fatigue and burnout', 'Make quick decisions under pressure',
      'Handle difficult passengers/situations', 'Lead crews effectively'
    ],
    other: [
      'Perform under pressure', 'Build confidence',
      'Handle stress and anxiety', 'Make decisions effectively',
      'Stay focused and motivated', 'Prevent burnout',
      'Improve communication', 'Lead with presence',
      'Handle difficult situations', 'Achieve peak performance'
    ]
  };

  const challengesByDomain = {
    athlete: [
      'Pre-game nerves', 'Choking under pressure', 'Negative self-talk',
      'Confidence issues', 'Difficulty focusing', 'Anger management',
      'Fear of failure', 'Inconsistent performance', 'Post-injury mental blocks',
      'Overthinking', 'Motivation issues', 'Post-mistake spiraling'
    ],
    executive: [
      'Meeting anxiety', 'Decision paralysis', 'Imposter syndrome',
      'Conflict avoidance', 'Perfectionism', 'Work-life imbalance',
      'Difficulty delegating', 'Public speaking fear', 'Handling criticism',
      'Managing up', 'Burnout', 'Emotional reactivity'
    ],
    communications: [
      'Crisis pressure', 'On-camera nerves', 'Hostile interviews',
      'Always being on call', 'Public criticism and backlash', 'Deadline stress',
      "Managing executives' expectations", 'Imposter syndrome', 'Decision fatigue',
      'Perfectionism', 'Work-life imbalance', 'Burnout'
    ],
    coach_sports: [
      'Game-day pressure', 'Losing streaks', 'Pressure from parents',
      'Administration and boosters', 'Job security', 'Recruiting stress',
      'Player discipline issues', 'Second-guessing my decisions', 'Long seasons and travel',
      'Taking losses home', 'Work-life imbalance', 'Burnout'
    ],
    coach_executive: [
      'Imposter feelings with senior clients', "Absorbing clients' stress", 'Uneven client flow',
      'Selling my services', 'Asking for my worth', 'Clients who stall',
      'Isolation in solo practice', 'Self-doubt after a tough session', 'Boundary issues',
      'Overwork', 'Work-life imbalance', 'Burnout'
    ],
    entrepreneur: [
      'Pitch anxiety', 'Fear of failure', 'Imposter syndrome',
      'Decision fatigue', 'Burnout and exhaustion', 'Isolation',
      'Handling investor rejection', 'Team conflict', 'Perfectionism blocking progress',
      'Comparison to others', 'Cash flow stress', 'Work-life boundaries'
    ],
    performer: [
      'Stage fright', 'Audition anxiety', 'Fear of judgment',
      'Perfectionism', 'Comparison to others', 'Creative blocks',
      'Handling rejection', 'Memory lapses', 'Self-doubt',
      'Post-performance crash', 'Vulnerability on stage', 'Career uncertainty'
    ],
    student: [
      'Test anxiety', 'Procrastination', 'Imposter syndrome',
      'Perfectionism', 'Fear of failure', 'Difficulty concentrating',
      'Overwhelm', 'Time management', 'Comparison to peers',
      'Interview nerves', 'Memory and recall issues', 'Burnout'
    ],
    sales: [
      'Rejection sensitivity', 'Cold call anxiety', 'Fear of hearing "no"',
      'Quota pressure', 'Confidence in pricing', 'Handling objections',
      'End-of-month panic', 'Burnout', 'Comparing to top performers',
      'Presentation nerves', 'Losing deals', 'Motivation slumps'
    ],
    medical: [
      'Decision anxiety', 'Fear of making mistakes', 'Emotional exhaustion',
      'Difficult conversations', 'Procedure anxiety', 'Sleep deprivation effects',
      'Compassion fatigue', 'Imposter syndrome', 'Handling loss',
      'Work-life imbalance', 'Team dynamics', 'Perfectionism'
    ],
    educator: [
      'Classroom management stress', 'Parent confrontation anxiety', 'Evaluation nerves',
      'Student behavior challenges', 'Administrative pressure', 'Compassion fatigue',
      'Work-life imbalance', 'Imposter syndrome', 'Burnout',
      'Difficult conversations', 'Feeling undervalued', 'Perfectionism'
    ],
    legal: [
      'Courtroom anxiety', 'Cross-examination pressure', 'Client expectations',
      'Opposing counsel tactics', 'Billable hour stress', 'Case outcome anxiety',
      'Public speaking fear', 'Imposter syndrome', 'Work-life imbalance',
      'Perfectionism', 'High-stakes negotiation nerves', 'Partner pressure'
    ],
    transportation: [
      'Pre-flight/pre-trip anxiety', 'Emergency situation stress', 'Fatigue management',
      'Difficult passenger interactions', 'Schedule pressure', 'Weather anxiety',
      'Decision-making under pressure', 'Isolation and loneliness', 'Work-life imbalance',
      'Safety responsibility weight', 'Physical demands', 'Irregular schedule stress'
    ],
    other: [
      'Performance anxiety', 'Imposter syndrome', 'Stress and overwhelm',
      'Decision fatigue', 'Fear of failure', 'Burnout',
      'Difficult conversations', 'Work-life imbalance', 'Confidence issues',
      'Handling criticism', 'Perfectionism', 'Motivation challenges'
    ]
  };

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const updateDomainContext = (key, value) => {
    setProfile(prev => ({
      ...prev,
      domain_context: {
        ...prev.domain_context,
        [key]: value
      }
    }));
  };

  // Pick-several lists inside domain_context. Older profiles may hold one string; treat it as a list of one.
  const domainList = (key) => {
    const cur = profile.domain_context[key];
    return Array.isArray(cur) ? cur : (cur ? [cur] : []);
  };
  const toggleDomainList = (key, value) => setProfile(prev => {
    const cur = prev.domain_context[key];
    const list = Array.isArray(cur) ? cur : (cur ? [cur] : []);
    return { ...prev, domain_context: { ...prev.domain_context,
      [key]: list.includes(value) ? list.filter(x => x !== value) : [...list, value] } };
  });

  const toggleGoal = (goal) => {
    setProfile(prev => ({
      ...prev,
      goals: prev.goals.includes(goal) 
        ? prev.goals.filter(g => g !== goal)
        : [...prev.goals, goal]
    }));
  };

  const toggleChallenge = (challenge) => {
    setProfile(prev => ({
      ...prev,
      challenges: prev.challenges.includes(challenge)
        ? prev.challenges.filter(c => c !== challenge)
        : [...prev.challenges, challenge]
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setError('');
    try {
      await API.post('/api/v1/mental-performance/profile', profile);
      navigate('/mental-performance');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setIsLoading(false);
    }
  };

  const canProceed = () => {
    switch(step) {
      case 1: return !!profile.program;
      case 2: return !!profile.performance_domain;
      case 3: return hasDomainDetails();
      case 4: return profile.goals.length > 0;
      case 5: return true;
      case 6: return true;
      default: return true;
    }
  };

  const hasDomainDetails = () => {
    const ctx = profile.domain_context;
    switch(profile.performance_domain) {
      case 'athlete': return ctx.sport && ctx.level;
      case 'executive': return ctx.role && ctx.industry;
      case 'communications': return ctx.role;
      case 'coach': return ctx.coach_type === 'sports' ? (ctx.coach_role && ctx.coach_level && ctx.coach_sport)
        : ctx.coach_type === 'executive' ? (ctx.coach_focus && domainList('coach_clients').length > 0) : false;
      case 'entrepreneur': return ctx.stage;
      case 'performer': return ctx.type;
      case 'student': return ctx.level;
      case 'sales': return ctx.role;
      case 'medical': return ctx.specialty;
      case 'educator': return ctx.level;
      case 'legal': return ctx.practice_area && ctx.role;
      case 'transportation': return ctx.sector && ctx.role;
      case 'other': return ctx.industry && ctx.job_title;
      default: return true;
    }
  };

  // ============================================================================
  // STYLING
  // ============================================================================

  const mpLife = { ...MP_EMPTY_LIFE, ...(profile.life_context || {}) };
  const mpFam = { ...MP_EMPTY_FAMILY, ...(mpLife.family || {}) };
  const setLife = (patch) => setProfile(p => ({ ...p, life_context: { ...MP_EMPTY_LIFE, ...(p.life_context || {}), ...patch } }));
  const setFam = (patch) => setProfile(p => {
    const l = { ...MP_EMPTY_LIFE, ...(p.life_context || {}) };
    return { ...p, life_context: { ...l, family: { ...MP_EMPTY_FAMILY, ...(l.family || {}), ...patch } } };
  });
  const famRows = (k) => (mpFam[k] || []);
  const setFamRow = (k, i, patch) => setFam({ [k]: famRows(k).map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const toggleToward = (v) => {
    const cur = mpLife.working_toward || [];
    if (cur.includes(v)) setLife({ working_toward: cur.filter(x => x !== v) });
    else if (cur.length < 2) setLife({ working_toward: [...cur, v] });
  };
  const listKey = profile.performance_domain === 'coach'
    ? 'coach_' + (profile.domain_context.coach_type || 'sports') : profile.performance_domain;
  const isChamp = profile.program === 'champ';
  const themeGradient = isChamp 
    ? 'linear-gradient(135deg, #F59E0B, #FCD34D)' 
    : 'linear-gradient(135deg, #3B82F6, #60A5FA)';
  const themeColor = isChamp ? '#F59E0B' : '#3B82F6';

  const cardStyle = (isSelected) => ({
    cursor: 'pointer',
    padding: '16px 20px',
    borderRadius: '12px',
    border: isSelected ? `3px solid ${themeColor}` : '2px solid rgba(255,255,255,0.2)',
    background: isSelected ? `${themeColor}15` : 'rgba(255,255,255,0.05)',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  });

  const chipStyle = (isSelected) => ({
    padding: '10px 16px',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '14px',
    border: isSelected ? `2px solid ${themeColor}` : '2px solid rgba(255,255,255,0.2)',
    background: isSelected ? `${themeColor}20` : 'transparent',
    color: isSelected ? themeColor : 'rgba(255,255,255,0.8)',
    transition: 'all 0.2s ease'
  });

  // ============================================================================
  // RENDER
  // ============================================================================

  const getDomainEmoji = () => {
    const domain = domains.find(d => d.value === profile.performance_domain);
    if (domain) return domain.emoji;
    if (isChamp) return '✝️';
    if (profile.program === 'ship') return '🚀';
    return '🏆';
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#1a1a2e',
      padding: '20px',
      color: 'white'
    }}>
      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        background: '#252547',
        borderRadius: '20px',
        padding: '30px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div style={{ fontSize: '48px', marginBottom: '10px' }}>
            {getDomainEmoji()}
          </div>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', margin: '0 0 5px 0' }}>
            {isEditing ? 'Edit Your Profile' : 'Mental Performance Setup'}
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0 }}>
            Step {step} of {totalSteps}
          </p>
          
          {/* Progress Bar */}
          <div style={{ marginTop: '15px', background: 'rgba(255,255,255,0.1)', borderRadius: '10px', height: '6px' }}>
            <div style={{
              width: `${(step / totalSteps) * 100}%`,
              background: profile.program ? themeGradient : 'linear-gradient(135deg, #667eea, #764ba2)',
              height: '100%',
              borderRadius: '10px',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.2)',
            border: '1px solid rgba(239, 68, 68, 0.5)',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '20px',
            color: '#FCA5A5'
          }}>
            {error}
          </div>
        )}

        {/* ================================================================ */}
        {/* STEP 1: Program Selection (CHAMP vs SHIP) */}
        {/* ================================================================ */}
        {step === 1 && (
          <div>
            <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
              Choose Your Program
            </h2>
            
            <div 
              onClick={() => setProfile({...profile, program:'champ'})}
              style={{
                cursor: 'pointer', padding: '24px', borderRadius: '16px', marginBottom: '15px',
                border: profile.program === 'champ' ? '3px solid #F59E0B' : '2px solid rgba(255,255,255,0.2)',
                background: profile.program === 'champ' ? 'rgba(245,158,11,0.1)' : 'rgba(255,255,255,0.05)',
                transition: 'all 0.3s ease'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ fontSize: '40px' }}>✝️</div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '22px', fontWeight: 'bold', color: '#F59E0B', margin: '0 0 4px 0' }}>CHAMP</h3>
                  <p style={{ color: 'rgba(255,255,255,0.7)', margin: '0 0 8px 0', fontSize: '14px' }}>
                    Christ-centered High-performance Mindset Program
                  </p>
                  <p style={{ fontStyle: 'italic', margin: 0, fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>
                    "Champions are Made Through Faith"
                  </p>
                </div>
                {profile.program === 'champ' && <div style={{ color: '#F59E0B', fontSize: '24px' }}>✓</div>}
              </div>
            </div>

            <div 
              onClick={() => setProfile({...profile, program:'ship'})}
              style={{
                cursor: 'pointer', padding: '24px', borderRadius: '16px', marginBottom: '25px',
                border: profile.program === 'ship' ? '3px solid #3B82F6' : '2px solid rgba(255,255,255,0.2)',
                background: profile.program === 'ship' ? 'rgba(59,130,246,0.1)' : 'rgba(255,255,255,0.05)',
                transition: 'all 0.3s ease'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ fontSize: '40px' }}>🚀</div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '22px', fontWeight: 'bold', color: '#3B82F6', margin: '0 0 4px 0' }}>SHIP</h3>
                  <p style={{ color: 'rgba(255,255,255,0.7)', margin: '0 0 8px 0', fontSize: '14px' }}>
                    Science-based High-performance Integrated Program
                  </p>
                  <p style={{ fontStyle: 'italic', margin: 0, fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>
                    "Master Your Mind, Master Your Performance"
                  </p>
                </div>
                {profile.program === 'ship' && <div style={{ color: '#3B82F6', fontSize: '24px' }}>✓</div>}
              </div>
            </div>
          </div>
        )}

        {/* ================================================================ */}
        {/* STEP 2: Domain Selection */}
        {/* ================================================================ */}
        {step === 2 && (
          <div>
            <h2 style={{ textAlign: 'center', marginBottom: '10px', fontWeight: '600' }}>
              What's Your Performance Arena?
            </h2>
            <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', marginBottom: '25px', fontSize: '14px' }}>
              Mental performance techniques adapt to your specific high-stakes environment
            </p>
            
            <div style={{ display: 'grid', gap: '12px' }}>
              {domains.map(domain => (
                <div 
                  key={domain.value}
                  onClick={() => setProfile({...profile, performance_domain: domain.value, domain_context: {}})}
                  style={cardStyle(profile.performance_domain === domain.value)}
                >
                  <div style={{ fontSize: '32px' }}>{domain.emoji}</div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600' }}>
                      {domain.name}
                    </h3>
                    <p style={{ margin: 0, fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
                      {domain.description}
                    </p>
                  </div>
                  {profile.performance_domain === domain.value && (
                    <div style={{ color: themeColor, fontSize: '20px' }}>✓</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ================================================================ */}
        {/* STEP 3: Domain-Specific Details */}
        {/* ================================================================ */}
        {step === 3 && (
          <div>
            {/* ATHLETE DETAILS */}
            {profile.performance_domain === 'athlete' && (
              <>
                <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
                  Tell us about your sport
                </h2>
                
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    What sport do you compete in?
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                    {sports.map(sport => (
                      <div
                        key={sport.name}
                        onClick={() => updateDomainContext('sport', sport.name)}
                        style={{
                          padding: '12px 8px',
                          borderRadius: '10px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          border: profile.domain_context.sport === sport.name 
                            ? `2px solid ${themeColor}` 
                            : '2px solid rgba(255,255,255,0.2)',
                          background: profile.domain_context.sport === sport.name 
                            ? `${themeColor}20` 
                            : 'transparent',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <div style={{ fontSize: '24px', marginBottom: '4px' }}>{sport.emoji}</div>
                        <div style={{ fontSize: '11px' }}>{sport.name}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Position (optional)
                  </label>
                  <input
                    type="text"
                    value={profile.domain_context.position || ''}
                    onChange={(e) => updateDomainContext('position', e.target.value)}
                    placeholder="e.g., Point Guard, Pitcher, Midfielder"
                    style={{
                      width: '100%',
                      padding: '14px',
                      borderRadius: '10px',
                      border: '2px solid rgba(255,255,255,0.2)',
                      background: 'rgba(255,255,255,0.05)',
                      color: 'white',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Competitive Level
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {athleteLevels.map(level => (
                      <div
                        key={level.value}
                        onClick={() => updateDomainContext('level', level.value)}
                        style={chipStyle(profile.domain_context.level === level.value)}
                      >
                        {level.label}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* COACH DETAILS */}
            {profile.performance_domain === 'coach' && (
              <>
                <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
                  Tell us about your coaching
                </h2>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    What kind of coaching do you do?
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {coachTypes.map(t => (
                      <div key={t.value} onClick={() => chooseCoachType(t.value)}
                        style={cardStyle(profile.domain_context.coach_type === t.value)}>
                        <div style={{ fontSize: '28px' }}>{t.emoji}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '16px', fontWeight: 600 }}>{t.label}</div>
                          <div style={{ fontSize: '14px', opacity: 0.75 }}>{t.note}</div>
                        </div>
                        {profile.domain_context.coach_type === t.value && (
                          <div style={{ color: themeColor }}>{'\u2713'}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {profile.domain_context.coach_type === 'sports' && (
                  <>
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                        Your Role
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {sportsCoachRoles.map(r => (
                          <div key={r.value} onClick={() => updateDomainContext('coach_role', r.value)}
                            style={chipStyle(profile.domain_context.coach_role === r.value)}>
                            {r.label}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                        Level You Coach
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {sportsCoachLevels.map(l => (
                          <div key={l.value} onClick={() => updateDomainContext('coach_level', l.value)}
                            style={chipStyle(profile.domain_context.coach_level === l.value)}>
                            {l.label}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                        Sport
                      </label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(92px, 1fr))', gap: '10px' }}>
                        {sports.map(sport => (
                          <div key={sport.name} onClick={() => updateDomainContext('coach_sport', sport.name)}
                            style={{
                              padding: '12px 6px', borderRadius: '10px', textAlign: 'center', cursor: 'pointer',
                              border: profile.domain_context.coach_sport === sport.name ? `2px solid ${themeColor}` : '2px solid rgba(255,255,255,0.2)',
                              background: profile.domain_context.coach_sport === sport.name ? `${themeColor}20` : 'transparent',
                              transition: 'all 0.2s ease'
                            }}>
                            <div style={{ fontSize: '24px', marginBottom: '4px' }}>{sport.emoji}</div>
                            <div style={{ fontSize: '13px' }}>{sport.name}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {profile.domain_context.coach_type === 'executive' && (
                  <>
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                        Your Coaching Focus
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {execCoachFocus.map(f => (
                          <div key={f.value} onClick={() => updateDomainContext('coach_focus', f.value)}
                            style={chipStyle(profile.domain_context.coach_focus === f.value)}>
                            {f.label}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                        Who You Coach (choose all that apply)
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {execCoachClients.map(c => (
                          <div key={c} onClick={() => toggleDomainList('coach_clients', c)}
                            style={chipStyle(domainList('coach_clients').includes(c))}>
                            {c}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                        How You Work (optional, choose all that apply)
                      </label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                        {execCoachPractice.map(p => (
                          <div key={p} onClick={() => toggleDomainList('coach_practice', p)}
                            style={chipStyle(domainList('coach_practice').includes(p))}>
                            {p}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}

            {/* EXECUTIVE DETAILS */}
            {profile.performance_domain === 'executive' && (
              <>
                <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
                  Tell us about your leadership role
                </h2>
                
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Your Role
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {executiveRoles.map(role => (
                      <div
                        key={role.value}
                        onClick={() => updateDomainContext('role', role.value)}
                        style={cardStyle(profile.domain_context.role === role.value)}
                      >
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: '15px' }}>{role.label}</span>
                        </div>
                        {profile.domain_context.role === role.value && (
                          <div style={{ color: themeColor }}>✓</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Industry
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {executiveIndustries.map(industry => (
                      <div
                        key={industry}
                        onClick={() => updateDomainContext('industry', industry)}
                        style={chipStyle(profile.domain_context.industry === industry)}
                      >
                        {industry}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Team Size (optional)
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {teamSizes.map(size => (
                      <div
                        key={size.value}
                        onClick={() => updateDomainContext('team_size', size.value)}
                        style={chipStyle(profile.domain_context.team_size === size.value)}
                      >
                        {size.label}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* COMMUNICATIONS EXECUTIVE DETAILS */}
            {profile.performance_domain === 'communications' && (
              <>
                <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
                  Tell us about your communications role
                </h2>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Your Role
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {[
                      { value: 'cco', label: 'Chief Communications Officer' },
                      { value: 'vp', label: 'VP of Communications' },
                      { value: 'director', label: 'Communications Director' },
                      { value: 'pr_media', label: 'PR / Media Relations' },
                      { value: 'spokesperson', label: 'Spokesperson / Press Secretary' },
                      { value: 'crisis', label: 'Crisis Communications' },
                      { value: 'internal', label: 'Internal / Employee Communications' },
                      { value: 'other', label: 'Other Communications Role' }
                    ].map(role => (
                      <div key={role.value} onClick={() => updateDomainContext('role', role.value)}
                        style={cardStyle(profile.domain_context.role === role.value)}>
                        <div style={{ flex: 1, fontSize: '15px' }}>{role.label}</div>
                        {profile.domain_context.role === role.value && (
                          <div style={{ color: themeColor }}>{'\u2713'}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Where you work (optional, choose all that apply)
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {['Corporate', 'Agency', 'Government / Public Sector', 'Nonprofit', 'Sports / Athletics',
                      'Media / Entertainment', 'Political / Campaign', 'Healthcare', 'Education', 'Other'].map(s => (
                      <div key={s} onClick={() => toggleDomainList('setting', s)}
                        style={chipStyle(domainList('setting').includes(s))}>
                        {s}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* ENTREPRENEUR DETAILS */}
            {profile.performance_domain === 'entrepreneur' && (
              <>
                <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
                  Tell us about your venture
                </h2>
                
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Business Stage
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                    {entrepreneurStages.map(stage => (
                      <div
                        key={stage.value}
                        onClick={() => updateDomainContext('stage', stage.value)}
                        style={cardStyle(profile.domain_context.stage === stage.value)}
                      >
                        <div style={{ fontSize: '24px' }}>{stage.emoji}</div>
                        <div style={{ flex: 1, fontSize: '14px' }}>{stage.label}</div>
                        {profile.domain_context.stage === stage.value && (
                          <div style={{ color: themeColor }}>✓</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Industry (optional)
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {entrepreneurIndustries.map(industry => (
                      <div
                        key={industry}
                        onClick={() => updateDomainContext('industry', industry)}
                        style={chipStyle(profile.domain_context.industry === industry)}
                      >
                        {industry}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* PERFORMER DETAILS */}
            {profile.performance_domain === 'performer' && (
              <>
                <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
                  Tell us about your craft
                </h2>
                
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Performance Type
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                    {performerTypes.map(type => (
                      <div
                        key={type.value}
                        onClick={() => updateDomainContext('type', type.value)}
                        style={cardStyle(profile.domain_context.type === type.value)}
                      >
                        <div style={{ fontSize: '24px' }}>{type.emoji}</div>
                        <div style={{ flex: 1, fontSize: '14px' }}>{type.label}</div>
                        {profile.domain_context.type === type.value && (
                          <div style={{ color: themeColor }}>✓</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Primary Medium (optional)
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {performerMediums.map(medium => (
                      <div
                        key={medium}
                        onClick={() => updateDomainContext('medium', medium)}
                        style={chipStyle(profile.domain_context.medium === medium)}
                      >
                        {medium}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* STUDENT DETAILS */}
            {profile.performance_domain === 'student' && (
              <>
                <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
                  Tell us about your academic journey
                </h2>
                
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Academic Level
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {studentLevels.map(level => (
                      <div
                        key={level.value}
                        onClick={() => updateDomainContext('level', level.value)}
                        style={cardStyle(profile.domain_context.level === level.value)}
                      >
                        <div style={{ flex: 1, fontSize: '15px' }}>{level.label}</div>
                        {profile.domain_context.level === level.value && (
                          <div style={{ color: themeColor }}>✓</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Field of Study (optional)
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {studentFields.map(field => (
                      <div
                        key={field}
                        onClick={() => updateDomainContext('field', field)}
                        style={chipStyle(profile.domain_context.field === field)}
                      >
                        {field}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Upcoming High-Stakes Event (optional)
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {studentExams.map(exam => (
                      <div
                        key={exam}
                        onClick={() => updateDomainContext('exam', exam)}
                        style={chipStyle(profile.domain_context.exam === exam)}
                      >
                        {exam}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* SALES DETAILS */}
            {profile.performance_domain === 'sales' && (
              <>
                <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
                  Tell us about your sales role
                </h2>
                
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Sales Role
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {salesRoles.map(role => (
                      <div
                        key={role.value}
                        onClick={() => updateDomainContext('role', role.value)}
                        style={cardStyle(profile.domain_context.role === role.value)}
                      >
                        <div style={{ flex: 1, fontSize: '15px' }}>{role.label}</div>
                        {profile.domain_context.role === role.value && (
                          <div style={{ color: themeColor }}>✓</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Sales Type (optional)
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {salesTypes.map(type => (
                      <div
                        key={type.value}
                        onClick={() => updateDomainContext('sales_type', type.value)}
                        style={chipStyle(profile.domain_context.sales_type === type.value)}
                      >
                        {type.label}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* MEDICAL DETAILS */}
            {profile.performance_domain === 'medical' && (
              <>
                <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
                  Tell us about your medical role
                </h2>
                
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Specialty
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {medicalSpecialties.map(specialty => (
                      <div
                        key={specialty}
                        onClick={() => updateDomainContext('specialty', specialty)}
                        style={chipStyle(profile.domain_context.specialty === specialty)}
                      >
                        {specialty}
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Role
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {medicalRoles.map(role => (
                      <div
                        key={role.value}
                        onClick={() => updateDomainContext('role', role.value)}
                        style={cardStyle(profile.domain_context.role === role.value)}
                      >
                        <div style={{ flex: 1, fontSize: '15px' }}>{role.label}</div>
                        {profile.domain_context.role === role.value && (
                          <div style={{ color: themeColor }}>✓</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Setting (optional)
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {medicalSettings.map(setting => (
                      <div
                        key={setting.value}
                        onClick={() => updateDomainContext('setting', setting.value)}
                        style={chipStyle(profile.domain_context.setting === setting.value)}
                      >
                        {setting.label}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
            {profile.performance_domain === 'educator' && (
              <>
                <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
                  Tell us about your teaching role
                </h2>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Grade Level
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {educatorLevels.map(level => (
                      <div
                        key={level.value}
                        onClick={() => updateDomainContext('level', level.value)}
                        style={cardStyle(profile.domain_context.level === level.value)}
                      >
                        <div style={{ flex: 1, fontSize: '15px' }}>{level.label}</div>
                        {profile.domain_context.level === level.value && (
                          <div style={{ color: themeColor }}>✓</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Subject Area(s) - Select all that apply
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {educatorSubjects.map(subject => (
                      <div
                        key={subject}
                        onClick={() => {
                          const current = profile.domain_context.subjects || [];
                          const updated = current.includes(subject) 
                            ? current.filter(s => s !== subject)
                            : [...current, subject];
                          updateDomainContext('subjects', updated);
                        }}
                        style={chipStyle((profile.domain_context.subjects || []).includes(subject))}
                      >
                        {subject}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    School Type (optional)
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {educatorSettings.map(setting => (
                      <div
                        key={setting.value}
                        onClick={() => updateDomainContext('setting', setting.value)}
                        style={chipStyle(profile.domain_context.setting === setting.value)}
                      >
                        {setting.label}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
            {profile.performance_domain === 'legal' && (
              <>
                <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
                  Tell us about your legal practice
                </h2>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Practice Area
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {legalPracticeAreas.map(area => (
                      <div
                        key={area}
                        onClick={() => updateDomainContext('practice_area', area)}
                        style={chipStyle(profile.domain_context.practice_area === area)}
                      >
                        {area}
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Role
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {legalRoles.map(role => (
                      <div
                        key={role.value}
                        onClick={() => updateDomainContext('role', role.value)}
                        style={cardStyle(profile.domain_context.role === role.value)}
                      >
                        <div style={{ flex: 1, fontSize: '15px' }}>{role.label}</div>
                        {profile.domain_context.role === role.value && (
                          <div style={{ color: themeColor }}>✓</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Setting (optional)
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {legalSettings.map(setting => (
                      <div
                        key={setting.value}
                        onClick={() => updateDomainContext('setting', setting.value)}
                        style={chipStyle(profile.domain_context.setting === setting.value)}
                      >
                        {setting.label}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* TRANSPORTATION DETAILS */}
            {profile.performance_domain === 'transportation' && (
              <>
                <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
                  Tell us about your transportation role
                </h2>
                
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    What sector do you work in?
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                    {transportationSectors.map(sector => (
                      <div
                        key={sector.value}
                        onClick={() => updateDomainContext('sector', sector.value)}
                        style={{
                          padding: '16px',
                          borderRadius: '10px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          border: profile.domain_context.sector === sector.value 
                            ? `2px solid ${themeColor}` 
                            : '2px solid rgba(255,255,255,0.2)',
                          background: profile.domain_context.sector === sector.value 
                            ? `${themeColor}20` 
                            : 'transparent',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <div style={{ fontSize: '28px', marginBottom: '6px' }}>{sector.emoji}</div>
                        <div style={{ fontSize: '14px' }}>{sector.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {profile.domain_context.sector === 'airline' && (
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                      Your Role
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {airlineRoles.map(role => (
                        <div
                          key={role.value}
                          onClick={() => updateDomainContext('role', role.value)}
                          style={cardStyle(profile.domain_context.role === role.value)}
                        >
                          <div style={{ flex: 1, fontSize: '15px' }}>{role.label}</div>
                          {profile.domain_context.role === role.value && (
                            <div style={{ color: themeColor }}>✓</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {profile.domain_context.sector === 'trucking' && (
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                      Your Role
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {truckingRoles.map(role => (
                        <div
                          key={role.value}
                          onClick={() => updateDomainContext('role', role.value)}
                          style={cardStyle(profile.domain_context.role === role.value)}
                        >
                          <div style={{ flex: 1, fontSize: '15px' }}>{role.label}</div>
                          {profile.domain_context.role === role.value && (
                            <div style={{ color: themeColor }}>✓</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {profile.domain_context.sector === 'transit' && (
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                      Your Role
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {transitRoles.map(role => (
                        <div
                          key={role.value}
                          onClick={() => updateDomainContext('role', role.value)}
                          style={cardStyle(profile.domain_context.role === role.value)}
                        >
                          <div style={{ flex: 1, fontSize: '15px' }}>{role.label}</div>
                          {profile.domain_context.role === role.value && (
                            <div style={{ color: themeColor }}>✓</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {profile.domain_context.sector && !['airline', 'trucking', 'transit'].includes(profile.domain_context.sector) && (
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                      Your Role/Title
                    </label>
                    <input
                      type="text"
                      value={profile.domain_context.role || ''}
                      onChange={(e) => updateDomainContext('role', e.target.value)}
                      placeholder="e.g., Captain, Conductor, Warehouse Manager"
                      style={{
                        width: '100%',
                        padding: '14px',
                        borderRadius: '10px',
                        border: '2px solid rgba(255,255,255,0.2)',
                        background: 'rgba(255,255,255,0.05)',
                        color: 'white',
                        fontSize: '16px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                )}

                <div>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Company/Employer (optional)
                  </label>
                  <input
                    type="text"
                    value={profile.domain_context.company || ''}
                    onChange={(e) => updateDomainContext('company', e.target.value)}
                    placeholder="e.g., Delta, UPS, Amtrak"
                    style={{
                      width: '100%',
                      padding: '14px',
                      borderRadius: '10px',
                      border: '2px solid rgba(255,255,255,0.2)',
                      background: 'rgba(255,255,255,0.05)',
                      color: 'white',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </>
            )}

            {/* OTHER INDUSTRY DETAILS */}
            {profile.performance_domain === 'other' && (
              <>
                <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
                  Tell us about your role
                </h2>
                
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    What industry do you work in?
                  </label>
                  <input
                    type="text"
                    value={profile.domain_context.industry || ''}
                    onChange={(e) => updateDomainContext('industry', e.target.value)}
                    placeholder="e.g., Construction, Agriculture, Hospitality, Government"
                    style={{
                      width: '100%',
                      padding: '14px',
                      borderRadius: '10px',
                      border: '2px solid rgba(255,255,255,0.2)',
                      background: 'rgba(255,255,255,0.05)',
                      color: 'white',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    What is your job title or role?
                  </label>
                  <input
                    type="text"
                    value={profile.domain_context.job_title || ''}
                    onChange={(e) => updateDomainContext('job_title', e.target.value)}
                    placeholder="e.g., Project Manager, Chef, Security Officer"
                    style={{
                      width: '100%',
                      padding: '14px',
                      borderRadius: '10px',
                      border: '2px solid rgba(255,255,255,0.2)',
                      background: 'rgba(255,255,255,0.05)',
                      color: 'white',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                    Brief description of what you do (optional)
                  </label>
                  <textarea
                    value={profile.domain_context.description || ''}
                    onChange={(e) => updateDomainContext('description', e.target.value)}
                    placeholder="Help us understand your role so we can personalize your coaching..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '14px',
                      borderRadius: '10px',
                      border: '2px solid rgba(255,255,255,0.2)',
                      background: 'rgba(255,255,255,0.05)',
                      color: 'white',
                      fontSize: '16px',
                      boxSizing: 'border-box',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {/* ================================================================ */}
        {/* STEP 4: Goals & Challenges */}
        {/* ================================================================ */}
        {step === 4 && (
          <div>
            <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
              What are you working on?
            </h2>
            
            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontWeight: '500' }}>
                Goals (select all that apply)
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {(goalsByDomain[listKey] || goalsByDomain.athlete).map(goal => (
                  <div
                    key={goal}
                    onClick={() => toggleGoal(goal)}
                    style={chipStyle(profile.goals.includes(goal))}
                  >
                    {goal}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '12px', fontWeight: '500' }}>
                Challenges (select all that apply)
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {(challengesByDomain[listKey] || challengesByDomain.athlete).map(challenge => (
                  <div
                    key={challenge}
                    onClick={() => toggleChallenge(challenge)}
                    style={chipStyle(profile.challenges.includes(challenge))}
                  >
                    {challenge}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ================================================================ */}
        {/* STEP 5: Preferences */}
        {/* ================================================================ */}
        {step === 5 && (
          <div>
            <h2 style={{ textAlign: 'center', marginBottom: '25px', fontWeight: '600' }}>
              Final Details
            </h2>
            
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                What should we call you?
              </label>
              <input
                type="text"
                value={profile.preferred_name}
                onChange={(e) => setProfile({...profile, preferred_name: e.target.value})}
                placeholder="Your preferred name"
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '10px',
                  border: '2px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)',
                  color: 'white',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                Years of experience (optional)
              </label>
              <input
                type="number"
                value={profile.years_experience || ''}
                onChange={(e) => setProfile({...profile, years_experience: parseInt(e.target.value) || null})}
                placeholder="e.g., 5"
                min="0"
                max="50"
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '10px',
                  border: '2px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)',
                  color: 'white',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                Coaching Style
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {['motivating', 'analytical', 'gentle', 'direct'].map(tone => (
                  <div
                    key={tone}
                    onClick={() => setProfile({...profile, coach_tone: tone})}
                    style={chipStyle(profile.coach_tone === tone)}
                  >
                    {tone.charAt(0).toUpperCase() + tone.slice(1)}
                  </div>
                ))}
              </div>
            </div>

            {profile.program === 'champ' && (
              <div>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                  Faith Background (optional)
                </label>
                <input
                  type="text"
                  value={profile.faith_background || ''}
                  onChange={(e) => setProfile({...profile, faith_background: e.target.value})}
                  placeholder="e.g., Protestant, Catholic, exploring faith"
                  style={{
                    width: '100%',
                    padding: '14px',
                    borderRadius: '10px',
                    border: '2px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.05)',
                    color: 'white',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* ================================================================ */}
        {/* STEP 6: Life & Family (optional) */}
        {/* ================================================================ */}
        {step === 6 && (() => {
          const lbl = { display: 'block', margin: '24px 0 10px', fontWeight: '600', fontSize: '16px' };
          const inp = { width: '100%', padding: '14px', borderRadius: '10px', border: '2px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.05)', color: 'white', fontSize: '16px', boxSizing: 'border-box', minWidth: 0 };
          const chip = (on) => ({ ...chipStyle(on), fontSize: '15px', padding: '11px 16px', color: 'white', fontWeight: on ? 700 : 500 });
          const chips = (opts, value, onPick) => (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {opts.map(o => {
                const on = Array.isArray(value) ? value.includes(o.value) : value === o.value;
                return <button key={o.value} type="button" aria-pressed={on} onClick={() => onPick(o.value)} style={chip(on)}>{on ? '\u2713 ' : ''}{o.label}</button>;
              })}
            </div>
          );
          const people = (k, noun) => (
            <>
              {famRows(k).map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input type="text" value={r.name || ''} maxLength={30} placeholder="First name (optional)" aria-label={noun + ' ' + (i + 1) + ' name'}
                    onChange={e => setFamRow(k, i, { name: e.target.value })} style={{ ...inp, flex: 1 }} />
                  <input type="number" inputMode="numeric" min="0" max="120" value={r.age ?? ''} placeholder="Age" aria-label={noun + ' ' + (i + 1) + ' age'}
                    onChange={e => setFamRow(k, i, { age: e.target.value })} style={{ ...inp, width: '84px', flex: '0 0 84px' }} />
                  <button type="button" onClick={() => setFam({ [k]: famRows(k).filter((_, j) => j !== i) })} aria-label={'Remove ' + noun + ' ' + (i + 1)}
                    style={{ width: '46px', flex: '0 0 46px', background: 'transparent', border: '2px solid rgba(255,255,255,0.2)', borderRadius: '10px', color: 'white', fontSize: '20px', cursor: 'pointer' }}>{'\u00D7'}</button>
                </div>
              ))}
              <button type="button" onClick={() => setFam({ [k]: [...famRows(k), { name: '', age: '' }] })}
                style={{ padding: '10px 14px', background: 'transparent', border: '1px dashed rgba(255,255,255,0.45)', borderRadius: '10px', color: 'white', fontSize: '15px', cursor: 'pointer' }}>+ Add a {noun.toLowerCase()}</button>
            </>
          );
          return (
            <div>
              <h2 style={{ textAlign: 'center', marginBottom: '8px', fontWeight: '600' }}>Life & Family</h2>
              <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.75)', margin: '0 0 8px', fontSize: '15px', lineHeight: 1.45 }}>
                Optional. It lets your coach talk with you about the people in your life by name. Share only what you want to.
              </p>
              <label style={lbl}>Relationship</label>
              {chips(MP_RELATIONSHIP, mpFam.relationship, v => setFam({ relationship: mpFam.relationship === v ? '' : v }))}
              {['married', 'relationship'].includes(mpFam.relationship) && (
                <>
                  <label style={lbl}>Your spouse or partner</label>
                  <div style={{ marginBottom: '10px' }}>
                    {chips(MP_ROLES, mpFam.partner_role, v => setFam({ partner_role: mpFam.partner_role === v ? '' : v }))}
                  </div>
                  <input type="text" value={mpFam.partner_name || ''} maxLength={30} placeholder="First name (optional)" aria-label="Spouse or partner's first name"
                    onChange={e => setFam({ partner_name: e.target.value })} style={inp} />
                </>
              )}
              <label style={lbl}>Children</label>
              {people('children', 'Child')}
              <label style={lbl}>Grandchildren</label>
              {people('grandchildren', 'Grandchild')}
              <label style={lbl}>Anyone else at home or close to you?</label>
              <input type="text" value={mpFam.other || ''} maxLength={120} placeholder="For example: my mom lives with us"
                onChange={e => setFam({ other: e.target.value })} style={inp} />
              <label style={lbl}>What takes up most of your days?</label>
              {chips(MP_SITUATION, mpLife.current_situation, v => setLife({ current_situation: mpLife.current_situation === v ? '' : v }))}
              <label style={lbl}>Outside of performance, what are you working toward? (up to two)</label>
              {chips(MP_TOWARD, mpLife.working_toward, toggleToward)}
              <label style={lbl}>Do you have a military connection?</label>
              {chips(MP_MILITARY, mpLife.military_status, v => setLife({ military_status: mpLife.military_status === v ? '' : v }))}
            </div>
          );
        })()}

        {/* ================================================================ */}
        {/* Navigation Buttons */}
        {/* ================================================================ */}
        <div style={{ marginTop: '30px', display: 'flex', gap: '12px' }}>
          {step > 1 && (
            <button
              onClick={() => setStep(step - 1)}
              style={{
                flex: 1,
                padding: '16px',
                borderRadius: '12px',
                border: '2px solid rgba(255,255,255,0.3)',
                background: 'transparent',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Back
            </button>
          )}
          
          {step < totalSteps ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              style={{
                flex: 2,
                padding: '16px',
                borderRadius: '12px',
                border: 'none',
                background: canProceed() ? themeGradient : 'rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                cursor: canProceed() ? 'pointer' : 'not-allowed',
                opacity: canProceed() ? 1 : 0.5
              }}
            >
              Continue
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              style={{
                flex: 2,
                padding: '16px',
                borderRadius: '12px',
                border: 'none',
                background: themeGradient,
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.7 : 1
              }}
            >
              {isLoading ? 'Saving...' : (isEditing ? 'Update Profile' : 'Start Training')}
            </button>
          )}
        </div>

        {/* Skip to chat link for returning users */}
        {isEditing && (
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button
              onClick={() => navigate('/mental-performance')}
              style={{
                background: 'none',
                border: 'none',
                color: 'rgba(255,255,255,0.6)',
                fontSize: '14px',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Skip and go to chat →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MentalPerformanceOnboarding;
